import type {Repository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {SortAscIcon, SortDescIcon, VerifiedIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Heading, Link, Pagination} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'

import type {Attestation, SortOrderType} from '../types'
import {SortOrder} from '../types'
import {AttestationListItem} from './AttestationListItem'

type AttestationsListProps = {
  repo: Repository
  attestations: Attestation[]
  totalCount: number
  onSortChange: (order: SortOrderType) => void
  onPageChange: (pageIndex: number) => void
  currentSort: SortOrderType
  pageCount: number
  currentPage: number
}

export function AttestationsList({
  repo,
  attestations,
  totalCount,
  onSortChange,
  onPageChange,
  currentSort,
  pageCount,
  currentPage,
}: AttestationsListProps) {
  if (attestations.length === 0) {
    return <BlankSlate />
  }

  const showSortDropdown = isFeatureEnabled('attestations_sorting')

  const sortDropdown = (
    <ActionMenu>
      <ActionMenu.Button
        leadingVisual={currentSort === SortOrder.Newest ? SortDescIcon : SortAscIcon}
        className="fg-muted"
        variant="invisible"
      >
        {currentSort === SortOrder.Newest ? 'Newest' : 'Oldest'}
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={() => onSortChange(SortOrder.Oldest)}>
            <ActionList.LeadingVisual>
              <SortAscIcon />
            </ActionList.LeadingVisual>
            Oldest
          </ActionList.Item>
          <ActionList.Item onSelect={() => onSortChange(SortOrder.Newest)}>
            <ActionList.LeadingVisual>
              <SortDescIcon />
            </ActionList.LeadingVisual>
            Newest
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  const table = (
    <div>
      <ListView
        variant="default"
        title="Attestations List"
        titleHeaderTag="h3"
        className={`border ${pageCount > 1 ? 'rounded-top-2' : 'rounded'}`}
        metadata={
          <ListViewMetadata title={`${totalCount} ${totalCount === 1 ? 'item' : 'items'}`}>
            {showSortDropdown && sortDropdown}
          </ListViewMetadata>
        }
      >
        {attestations.map(attestation => (
          <AttestationListItem key={attestation.id} repo={repo} attestation={attestation} />
        ))}
      </ListView>
    </div>
  )

  return (
    <div>
      {table}
      {pageCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            px: 3,
            py: '10px',
            borderBottomWidth: 1,
            borderRightWidth: 1,
            borderLeftWidth: 1,
            borderTopWidth: 0,
            borderStyle: 'solid',
            borderColor: 'border.default',
            borderEndStartRadius: 6,
            borderEndEndRadius: 6,
            '> nav': {
              margin: 0,
            },
          }}
        >
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            marginPageCount={1}
            onPageChange={(e, pageIndex) => {
              e.preventDefault()
              onPageChange(pageIndex)
            }}
            showPages={false}
          />
        </Box>
      )}
    </div>
  )
}

const BlankSlate = () => {
  return (
    <Box
      sx={{
        margin: '0 auto',
        borderColor: 'border.default',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 6,
      }}
    >
      <div className="blankslate">
        <Octicon icon={VerifiedIcon} size={30} sx={{color: 'fg.subtle', mb: 3}} />
        <Heading as="h2" className="blankslate-heading" sx={{fontSize: 4, mb: 2}}>
          No attestations
        </Heading>
        <p>
          Create attestations using the{' '}
          <Link inline href="https://github.com/actions/attest-build-provenance" target="_blank">
            attest-build-provenance
          </Link>{' '}
          action
        </p>
      </div>
    </Box>
  )
}

try{ AttestationsList.displayName ||= 'AttestationsList' } catch {}
try{ BlankSlate.displayName ||= 'BlankSlate' } catch {}